import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Minus, Plus, ShoppingBag, Zap, ChevronLeft, ChevronRight, Maximize2, X, Play } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import StarRating from "@/components/StarRating";
import ProductCard from "@/components/ProductCard";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWishlisted, setCartOpen } = useStore();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [dir, setDir] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1); setActiveImg(0); setDir(0);
    api.get(`/products/${slug}`).then(({ data }) => {
      setData(data);
      document.title = `${data.product.name} — Dignity Food Kitchen`;
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  if (!data) return <div className="grid min-h-screen place-items-center bg-black text-gold font-oswald">Loading…</div>;
  const p = data.product;
  const wished = isWishlisted(p.id);
  const media = [
    ...p.images.map((s) => ({ type: "image", src: s })),
    ...(p.videos || []).map((s) => ({ type: "video", src: s })),
  ];
  const total = media.length;
  const current = media[activeImg] || media[0];
  const goTo = (i) => { setDir(i > activeImg ? 1 : -1); setActiveImg((i + total) % total); };
  const next = () => { setDir(1); setActiveImg((activeImg + 1) % total); };
  const prev = () => { setDir(-1); setActiveImg((activeImg - 1 + total) % total); };

  const buyNow = async () => { await addToCart(p, qty, false); navigate("/checkout"); };

  return (
    <div className="min-h-screen bg-black pt-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/shop" className="inline-flex items-center gap-1 py-6 font-oswald text-xs uppercase tracking-[0.15em] text-white/50 hover:text-gold">
          <ChevronLeft size={16} /> Back to Shop
        </Link>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Gallery — sticky, swipeable */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div
              className="group relative aspect-square touch-pan-y overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#0f1b15] to-black"
              data-testid="product-main-image">
              <div className="absolute inset-0 z-0 opacity-30" style={{ background: `radial-gradient(circle at 50% 30%, ${p.accent}, transparent 60%)` }} />

              {(p.high_protein || p.iron_focused) && (
                <div className="absolute left-5 top-5 z-20 flex flex-col items-start gap-1.5">
                  {p.high_protein && (
                    <span className="rounded-full gold-gradient px-3 py-1 font-oswald text-[11px] font-semibold uppercase tracking-[0.12em] text-black shadow-lg">
                      High Protein
                    </span>
                  )}
                  {p.iron_focused && (
                    <span className="rounded-full border border-gold/60 bg-black/70 px-3 py-1 font-oswald text-[11px] font-semibold uppercase tracking-[0.12em] text-gold backdrop-blur">
                      Iron-Focused
                    </span>
                  )}
                </div>
              )}

              <button onClick={() => setLightbox(true)} aria-label="Zoom image" data-testid="gallery-expand"
                className="absolute right-5 top-5 z-20 grid h-10 w-10 place-items-center rounded-full glass text-cream transition-transform hover:scale-110 hover:text-gold">
                <Maximize2 size={17} />
              </button>

              <AnimatePresence initial={false} custom={dir} mode="popLayout">
                {current.type === "video" ? (
                  <motion.video
                    key={activeImg} src={current.src} autoPlay loop muted playsInline
                    custom={dir}
                    initial={{ opacity: 0, x: dir >= 0 ? 80 : -80 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: dir >= 0 ? -80 : 80 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => setLightbox(true)}
                    className="absolute inset-0 z-10 h-full w-full cursor-zoom-in object-cover" />
                ) : (
                  <motion.img
                    key={activeImg} src={current.src} alt={`${p.name} — ${p.flavour} view ${activeImg + 1}`}
                    custom={dir}
                    initial={{ opacity: 0, x: dir >= 0 ? 80 : -80 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: dir >= 0 ? -80 : 80 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    drag={total > 1 ? "x" : false}
                    dragConstraints={{ left: 0, right: 0 }} dragElastic={0.18}
                    onDragEnd={(e, info) => {
                      if (info.offset.x < -70) next();
                      else if (info.offset.x > 70) prev();
                    }}
                    onClick={() => setLightbox(true)}
                    className="absolute inset-0 z-10 h-full w-full cursor-zoom-in object-cover product-shadow transition-transform duration-700 active:cursor-grabbing group-hover:scale-[1.06]" />
                )}
              </AnimatePresence>

              {total > 1 && (
                <>
                  <button onClick={prev} aria-label="Previous image" data-testid="gallery-prev"
                    className="absolute left-3 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full glass text-cream transition-transform hover:scale-110 hover:text-gold">
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={next} aria-label="Next image" data-testid="gallery-next"
                    className="absolute right-3 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full glass text-cream transition-transform hover:scale-110 hover:text-gold">
                    <ChevronRight size={20} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-2">
                    {media.map((_, i) => (
                      <button key={i} onClick={() => goTo(i)} aria-label={`Go to media ${i + 1}`}
                        data-testid={`gallery-dot-${i}`}
                        className={`h-2 rounded-full transition-all ${activeImg === i ? "w-6 gold-gradient" : "w-2 bg-white/40"}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {total > 1 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {media.map((m, i) => (
                  <button key={i} onClick={() => goTo(i)} data-testid={`thumb-${i}`}
                    className={`relative h-20 w-20 overflow-hidden rounded-xl border-2 transition-colors ${activeImg === i ? "border-gold" : "border-white/10"}`}>
                    {m.type === "video" ? (
                      <>
                        <video src={m.src} muted playsInline className="h-full w-full object-cover" />
                        <span className="absolute inset-0 grid place-items-center bg-black/45">
                          <Play size={16} className="text-white" fill="white" />
                        </span>
                      </>
                    ) : (
                      <img src={m.src} alt={`${p.name} view ${i + 1}`} className="h-full w-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Full-screen lightbox */}
          <AnimatePresence>
            {lightbox && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[95] flex items-center justify-center bg-black/95 backdrop-blur-md"
                data-testid="gallery-lightbox" onClick={() => setLightbox(false)}>
                <button onClick={() => setLightbox(false)} aria-label="Close" data-testid="lightbox-close"
                  className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full glass text-cream hover:text-gold">
                  <X size={22} />
                </button>
                <AnimatePresence initial={false} custom={dir} mode="wait">
                  {current.type === "video" ? (
                    <motion.video key={activeImg} src={current.src} controls autoPlay loop playsInline
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()}
                      className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl" />
                  ) : (
                    <motion.img key={activeImg} src={current.src} alt={`${p.name} enlarged view ${activeImg + 1}`}
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()}
                      className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl" />
                  )}
                </AnimatePresence>
                {total > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous"
                      className="absolute left-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full glass text-cream hover:text-gold">
                      <ChevronLeft size={24} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next"
                      className="absolute right-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full glass text-cream hover:text-gold">
                      <ChevronRight size={24} />
                    </button>
                    <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2">
                      {media.map((_, i) => (
                        <button key={i} onClick={(e) => { e.stopPropagation(); goTo(i); }} aria-label={`Media ${i + 1}`}
                          className={`h-2 rounded-full transition-all ${activeImg === i ? "w-6 gold-gradient" : "w-2 bg-white/40"}`} />
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Details */}
          <div className="pb-16">
            <span className="font-oswald text-sm uppercase tracking-[0.2em]" style={{ color: p.accent }}>{p.flavour}</span>
            <h1 className="mt-2 font-display text-5xl leading-none tracking-tight text-cream sm:text-6xl">{p.name}</h1>
            <div className="mt-4 flex items-center gap-3">
              <StarRating rating={p.rating} size={16} />
              <span className="text-sm text-white/50">{p.rating} · {p.reviews_count} reviews</span>
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-5xl text-gold">{inr(p.price)}</span>
              <span className="text-lg text-white/30 line-through">{inr(p.mrp)}</span>
              <span className="rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-400">
                {Math.round((1 - p.price / p.mrp) * 100)}% OFF
              </span>
            </div>
            <p className="mt-1 text-sm text-white/40">{p.weight} · Inclusive of all taxes</p>

            <p className="mt-6 leading-relaxed text-white/60">{p.long_desc}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {p.badges.slice(0, 5).map((b) => (
                <span key={b} className="rounded-full border border-gold/25 px-3 py-1 font-oswald text-[11px] uppercase tracking-wide text-gold/90">{b}</span>
              ))}
            </div>

            {/* qty + actions */}
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center gap-3 rounded-full border border-white/15 px-2 py-1">
                <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease" data-testid="qty-dec"
                  className="grid h-9 w-9 place-items-center text-white/70 hover:text-gold"><Minus size={16} /></button>
                <span className="w-6 text-center font-oswald text-lg text-cream" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} aria-label="Increase" data-testid="qty-inc"
                  className="grid h-9 w-9 place-items-center text-white/70 hover:text-gold"><Plus size={16} /></button>
              </div>
              <button onClick={() => toggleWishlist(p)} data-testid="pdp-wishlist"
                className="grid h-12 w-12 place-items-center rounded-full border border-white/15 hover:border-gold">
                <Heart size={18} className={wished ? "fill-red-500 text-red-500" : "text-white/70"} />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => addToCart(p, qty)} data-testid="pdp-add-to-cart"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-gold px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                <ShoppingBag size={17} /> Add to Cart
              </button>
              <button onClick={buyNow} data-testid="pdp-buy-now"
                className="flex flex-1 items-center justify-center gap-2 rounded-full gold-gradient px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-[1.02]">
                <Zap size={17} /> Buy Now
              </button>
            </div>

            {/* Info accordions */}
            <Accordion type="single" collapsible defaultValue="nutrition" className="mt-10">
              <AccordionItem value="why" className="border-white/10">
                <AccordionTrigger className="font-oswald uppercase tracking-wide text-cream">Why You'll Love It</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-white/60">
                    {p.why_love.map((w, i) => <li key={i} className="flex gap-2"><span className="text-gold">✦</span>{w}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ingredients" className="border-white/10">
                <AccordionTrigger className="font-oswald uppercase tracking-wide text-cream">Ingredients</AccordionTrigger>
                <AccordionContent>
                  <p className="text-white/60">{p.ingredients.join(", ")}.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="nutrition" className="border-white/10">
                <AccordionTrigger className="font-oswald uppercase tracking-wide text-cream">Nutrition Information</AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-hidden rounded-2xl bg-[#F5F5F0] text-[#0A1712]">
                    <div className="flex items-center justify-between border-b border-black/10 px-5 py-3 text-sm font-semibold">
                      <span>Per {p.nutrition.serving_size} serving</span>
                      <span>{p.nutrition.servings_per_pack} servings / pack</span>
                    </div>
                    <table className="w-full text-sm" data-testid="nutrition-table">
                      <tbody>
                        {p.nutrition.rows.map((r, i) => (
                          <tr key={i} className={i % 2 ? "bg-black/[0.03]" : ""}>
                            <td className="px-5 py-2.5">{r.label}</td>
                            <td className="px-5 py-2.5 text-right font-semibold">{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="px-5 py-3 text-xs text-[#0A1712]/50">{p.nutrition.note}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="allergen" className="border-white/10">
                <AccordionTrigger className="font-oswald uppercase tracking-wide text-cream">Allergen Information</AccordionTrigger>
                <AccordionContent><p className="text-white/60">{p.allergens}</p></AccordionContent>
              </AccordionItem>

              <AccordionItem value="storage" className="border-white/10">
                <AccordionTrigger className="font-oswald uppercase tracking-wide text-cream">Storage Instructions</AccordionTrigger>
                <AccordionContent><p className="text-white/60">{p.storage}</p></AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Related */}
        <div className="border-t border-white/10 py-20">
          <h2 className="font-display text-4xl tracking-tight text-cream sm:text-5xl">YOU MIGHT ALSO <span className="text-gold-gradient">CRAVE</span></h2>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {data.related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
